<template>
  <div class="vendor-work-history-page">
    <section class="page-header">
      <div>
        <p class="page-eyebrow">Vendor Work History</p>
        <div class="page-title-row">
          <h1>業者作業履歴集計</h1>
          <span class="period-badge">{{ selectedPeriodLabel }}</span>
        </div>
      </div>

      <div class="header-actions">
        <button
          type="button"
          class="btn btn-success"
          :disabled="loading || exporting || !hasRows"
          @click="exportToExcel"
        >
          {{ exporting ? '出力中...' : 'Excel出力' }}
        </button>

        <router-link :to="{ name: 'Home' }" class="btn btn-outline-primary">
          ホーム
        </router-link>
      </div>
    </section>

    <section class="search-panel">
      <div class="panel-heading">
        <h2>検索条件</h2>
        <span v-if="lastLoadedAt" class="loaded-at">更新 {{ formatDateTime(lastLoadedAt) }}</span>
      </div>

      <div v-if="optionLoadError" class="alert alert-warning mb-3">
        {{ optionLoadError }}
      </div>

      <div class="filter-grid">
        <label class="filter-field">
          <span>開始年月</span>
          <input
            v-model="searchForm.start_ym"
            type="month"
            class="form-control"
            :disabled="loading"
            @change="handleStartYmChange"
          />
        </label>

        <label class="filter-field">
          <span>終了年月</span>
          <input
            v-model="searchForm.end_ym"
            type="month"
            class="form-control"
            :min="searchForm.start_ym"
            :disabled="loading"
            @change="handleEndYmChange"
          />
        </label>

        <label class="filter-field">
          <span>作業種別</span>
          <select v-model="searchForm.work_type" class="form-select" :disabled="loading">
            <option v-for="option in workTypeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="filter-field filter-field-wide">
          <span>キーワード</span>
          <input
            v-model="searchForm.keyword"
            type="search"
            class="form-control"
            placeholder="業者名・建物名・顧客名・作業項目"
            :disabled="loading"
            @keyup.enter="applyFilters"
          />
        </label>

        <label class="filter-field">
          <span>業者</span>
          <VendorSelectBox
            v-model="searchForm.vendor_id"
            :options="vendorOptions"
            placeholder="業者名で検索"
            :formatter="vendorOptionLabel"
          />
        </label>

        <label class="filter-field">
          <span>顧客</span>
          <CustomerSelectBox
            v-model="searchForm.customer_id"
            :options="customerOptions"
            placeholder="顧客名で検索"
            :formatter="customerOptionLabel"
          />
        </label>

        <label class="filter-field">
          <span>建物</span>
          <BuildingSelectBox
            v-model="searchForm.building_id"
            :options="buildingOptions"
            placeholder="建物名で検索"
            :formatter="buildingOptionLabel"
          />
        </label>

        <label class="filter-field">
          <span>業務区分</span>
          <SelectableSearch
            v-model="searchForm.business_classification_id"
            :options="businessClassificationOptions"
            idKey="id"
            labelKey="business_classification_nm"
            placeholder="業務区分で検索"
            :formatter="businessClassificationOptionLabel"
            :getInputLabel="businessClassificationInputLabel"
          />
        </label>

        <label class="filter-field">
          <span>金額状態</span>
          <select v-model="searchForm.amount_status" class="form-select" :disabled="loading">
            <option v-for="option in amountStatusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="filter-field">
          <span>依頼金額 下限</span>
          <input
            v-model="searchForm.amount_min"
            type="number"
            min="0"
            step="1"
            class="form-control"
            placeholder="0"
            :disabled="loading"
            @keyup.enter="applyFilters"
          />
        </label>

        <label class="filter-field">
          <span>依頼金額 上限</span>
          <input
            v-model="searchForm.amount_max"
            type="number"
            min="0"
            step="1"
            class="form-control"
            placeholder="100000"
            :disabled="loading"
            @keyup.enter="applyFilters"
          />
        </label>

        <div class="filter-actions">
          <button type="button" class="btn btn-primary" :disabled="loading" @click="applyFilters">
            検索
          </button>
          <button type="button" class="btn btn-light" :disabled="loading" @click="resetFilters">
            クリア
          </button>
        </div>
      </div>

      <div v-if="activeFilterChips.length" class="active-filter-list">
        <span v-for="chip in activeFilterChips" :key="chip.label" class="filter-chip">
          <span>{{ chip.label }}</span>
          <strong>{{ chip.value }}</strong>
        </span>
      </div>
    </section>

    <section v-if="hasRows && !loading" class="summary-grid">
      <article v-for="card in summaryCards" :key="card.label" class="summary-card">
        <span>{{ card.label }}</span>
        <strong>{{ card.value }}</strong>
        <small>{{ card.note }}</small>
      </article>
    </section>

    <div v-if="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div v-if="exportErrorMessage" class="alert alert-danger">
      {{ exportErrorMessage }}
    </div>

    <section class="table-section">
      <div class="table-toolbar">
        <div>
          <h2>業者作業履歴</h2>
          <span>{{ resultOverviewLabel }}</span>
        </div>
        <div class="table-toolbar-total">
          <span>税抜合計</span>
          <strong>{{ formatCurrency(yearlyTotal) }}</strong>
        </div>
      </div>

      <div v-if="loading" class="state-panel">
        <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
        <p>読み込み中です。</p>
      </div>

      <div v-else-if="!hasRows" class="state-panel">
        <h3>対象データがありません</h3>
        <p>検索条件を変更して再度検索してください。</p>
      </div>

      <div v-else class="table-scroll">
        <table class="history-table" :style="historyTableStyle">
          <caption class="visually-hidden">
            {{ selectedPeriodLabel }} 業者作業履歴集計
          </caption>
          <thead>
            <tr>
              <th class="sticky-col vendor-col">業者名</th>
              <th class="sticky-col vendor-total-col text-end">業者計</th>
              <th class="sticky-col building-col">建物名</th>
              <th class="customer-col">顧客名</th>
              <th class="type-col">種別</th>
              <th class="classification-col">業務区分</th>
              <th class="work-content-col">作業項目</th>
              <th v-for="periodMonth in periodMonths" :key="periodMonth.key" class="month-col text-end">
                {{ periodMonth.label }}
              </th>
              <th class="total-col text-end">計</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="schedule in displayedSchedules" :key="schedule.vendor_id">
              <tr
                v-for="(workContent, workContentIdx) in schedule.work_contents"
                :key="buildWorkContentKey(schedule, workContent, workContentIdx)"
              >
                <td
                  v-if="workContentIdx === 0"
                  :rowspan="schedule.work_contents.length"
                  class="sticky-col vendor-col vendor-name-cell"
                  :title="schedule.vendor_name"
                >
                  {{ schedule.vendor_name }}
                </td>
                <td
                  v-if="workContentIdx === 0"
                  :rowspan="schedule.work_contents.length"
                  class="sticky-col vendor-total-col amount-cell vendor-total-cell"
                >
                  {{ formatCurrency(calculateGroupTotal(schedule.work_contents)) }}
                </td>
                <td class="sticky-col building-col text-truncate-cell" :title="workContent.building_nm">
                  {{ workContent.building_nm || '未設定' }}
                </td>
                <td class="customer-col text-truncate-cell" :title="workContent.customer_nm">
                  {{ workContent.customer_nm || '未設定' }}
                </td>
                <td class="type-col">
                  <span :class="['type-badge', workContent.type === '年間予定業務' ? 'annual' : 'temporary']">
                    {{ workContent.type }}
                  </span>
                </td>
                <td class="classification-col text-truncate-cell" :title="workContent.business_classification_nm">
                  {{ workContent.business_classification_nm || '未設定' }}
                </td>
                <td class="work-content-col text-truncate-cell" :title="workContent.work_content">
                  {{ workContent.work_content || '未設定' }}
                </td>
                <td v-for="periodMonth in periodMonths" :key="periodMonth.key" class="month-col amount-cell">
                  {{ formatDetailAmount(workContent.monthly_details, periodMonth.key) }}
                </td>
                <td class="total-col amount-cell row-total-cell">
                  {{ formatCurrency(calculateTotal(workContent.monthly_details)) }}
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr class="subtotal-row">
              <td colspan="7">月別依頼金額</td>
              <td v-for="(total, index) in monthlyBillTotals" :key="`bill-${index}`" class="amount-cell">
                {{ formatCurrency(total) }}
              </td>
              <td class="amount-cell">{{ formatCurrency(yearlyTotal) }}</td>
            </tr>
            <tr class="tax-row">
              <td colspan="7">消費税</td>
              <td v-for="(tax, index) in monthlyTaxes" :key="`tax-${index}`" class="amount-cell">
                {{ formatCurrency(tax) }}
              </td>
              <td class="amount-cell">{{ formatCurrency(totalTax) }}</td>
            </tr>
            <tr class="grand-total-row">
              <td colspan="7">税込合計</td>
              <td v-for="(grandTotal, index) in monthlyGrandTotals" :key="`grand-${index}`" class="amount-cell">
                {{ formatCurrency(grandTotal) }}
              </td>
              <td class="amount-cell">{{ formatCurrency(grandYearlyTotal) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as XLSX from 'xlsx';
import {
  getBusinessClassifications,
  getSelectBoxBuildings,
  getSelectBoxCustomers,
  getSelectBoxVendors,
  getVendorAnnualSchedules,
} from '@/api/axios';
import {
  compareYmValue,
  formatPeriodLabel,
  formatYmLabel,
  formatYmValue,
  getDefaultYmRange,
  parseYmValue,
} from '@/utils/aggregateDashboard';
import BuildingSelectBox from '@/components/searchSelectBoxes/BuildingSelectBox.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import SelectableSearch from '@/components/searchSelectBoxes/SelectableSearch.vue';
import VendorSelectBox from '@/components/searchSelectBoxes/VendorSelectBox.vue';

const workTypeOptions = [
  { value: 'all', label: 'すべて' },
  { value: 'annual', label: '年間予定業務' },
  { value: 'temporary', label: '臨時業務' },
];

const amountStatusOptions = [
  { value: '', label: 'すべて' },
  { value: 'with_amount', label: '金額あり' },
  { value: 'zero_or_empty', label: '0円・未設定' },
];

const filterKeys = [
  'start_ym',
  'end_ym',
  'work_type',
  'vendor_id',
  'customer_id',
  'building_id',
  'business_classification_id',
  'keyword',
  'amount_min',
  'amount_max',
  'amount_status',
];

const createDefaultFilters = () => {
  const defaultRange = getDefaultYmRange();

  return {
    start_ym: defaultRange.startYm,
    end_ym: defaultRange.endYm,
    work_type: 'all',
    vendor_id: '',
    customer_id: '',
    building_id: '',
    business_classification_id: '',
    keyword: '',
    amount_min: '',
    amount_max: '',
    amount_status: '',
  };
};

const normalizeIntegerText = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '';
  return String(Math.max(0, Math.trunc(parsed)));
};

const normalizeFilters = (filters = {}) => {
  const defaults = createDefaultFilters();
  const normalized = filterKeys.reduce((acc, key) => {
    acc[key] = String(filters[key] ?? defaults[key] ?? '').trim();
    return acc;
  }, {});

  normalized.start_ym = parseYmValue(normalized.start_ym) ? normalized.start_ym : defaults.start_ym;
  normalized.end_ym = parseYmValue(normalized.end_ym) ? normalized.end_ym : normalized.start_ym;

  if (compareYmValue(normalized.end_ym, normalized.start_ym) < 0) {
    normalized.end_ym = normalized.start_ym;
  }

  if (!workTypeOptions.some((option) => option.value === normalized.work_type)) {
    normalized.work_type = defaults.work_type;
  }

  if (!amountStatusOptions.some((option) => option.value === normalized.amount_status)) {
    normalized.amount_status = '';
  }

  normalized.amount_min = normalizeIntegerText(normalized.amount_min);
  normalized.amount_max = normalizeIntegerText(normalized.amount_max);

  if (normalized.amount_min && normalized.amount_max && Number(normalized.amount_min) > Number(normalized.amount_max)) {
    [normalized.amount_min, normalized.amount_max] = [normalized.amount_max, normalized.amount_min];
  }

  return normalized;
};

const getQueryValue = (queryValue) => {
  if (Array.isArray(queryValue)) return queryValue[0] ?? '';
  return queryValue ?? '';
};

const filtersFromQuery = (query = {}) => {
  const defaults = createDefaultFilters();
  const queryFilters = filterKeys.reduce((acc, key) => {
    acc[key] = getQueryValue(query[key]) || defaults[key];
    return acc;
  }, {});

  if (!getQueryValue(query.start_ym) && getQueryValue(query.year)) {
    const year = Number(getQueryValue(query.year));
    const month = Number(getQueryValue(query.month));

    if (Number.isInteger(year) && Number.isInteger(month) && month >= 1 && month <= 12) {
      queryFilters.start_ym = formatYmValue(year, month);
      queryFilters.end_ym = queryFilters.start_ym;
    } else if (Number.isInteger(year)) {
      queryFilters.start_ym = formatYmValue(year, 7);
      queryFilters.end_ym = formatYmValue(year + 1, 6);
    }
  }

  return normalizeFilters(queryFilters);
};

const buildApiParams = (filters) => {
  const params = {};
  filterKeys.forEach((key) => {
    if (key === 'work_type' && filters[key] === 'all') return;
    if (filters[key] !== '') params[key] = filters[key];
  });
  return params;
};

const buildRouteQuery = (filters) => {
  const defaults = createDefaultFilters();
  return filterKeys.reduce((query, key) => {
    if (key === 'work_type' && filters[key] === defaults[key]) return query;
    if (filters[key] !== defaults[key] && filters[key] !== '') query[key] = filters[key];
    return query;
  }, {});
};

const sanitizeFilePart = (value) => String(value || '')
  .replace(/[\\/:*?"<>|]/g, '_')
  .replace(/\s+/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 80);

const formatTimestampForFilename = (dateValue = new Date()) => {
  const parts = [
    dateValue.getFullYear(),
    String(dateValue.getMonth() + 1).padStart(2, '0'),
    String(dateValue.getDate()).padStart(2, '0'),
    String(dateValue.getHours()).padStart(2, '0'),
    String(dateValue.getMinutes()).padStart(2, '0'),
  ];

  return parts.join('');
};

const buildPeriodMonths = (startYm, endYm) => {
  const start = parseYmValue(startYm);
  const end = parseYmValue(endYm);
  if (!start || !end) return [];

  const months = [];
  const currentDate = new Date(start.year, start.month - 1, 1);
  const endDate = new Date(end.year, end.month - 1, 1);

  while (currentDate <= endDate) {
    const key = formatYmValue(currentDate.getFullYear(), currentDate.getMonth() + 1);
    months.push({
      key,
      label: formatYmLabel(key),
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
    });
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

export default {
  name: 'VendorWorkHistory',
  components: {
    BuildingSelectBox,
    CustomerSelectBox,
    SelectableSearch,
    VendorSelectBox,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const initialFilters = filtersFromQuery(route.query);

    const schedules = ref([]);
    const searchForm = ref({ ...initialFilters });
    const appliedFilters = ref({ ...initialFilters });
    const loading = ref(false);
    const exporting = ref(false);
    const errorMessage = ref('');
    const exportErrorMessage = ref('');
    const optionLoadError = ref('');
    const lastLoadedAt = ref(null);
    const vendorOptions = ref([]);
    const customerOptions = ref([]);
    const buildingOptions = ref([]);
    const businessClassificationOptions = ref([]);

    const selectedPeriodLabel = computed(() => {
      return formatPeriodLabel(appliedFilters.value.start_ym, appliedFilters.value.end_ym);
    });

    watchEffect(() => {
      document.title = `${selectedPeriodLabel.value} 業者作業履歴集計`;
    });

    const periodMonths = computed(() => {
      return buildPeriodMonths(appliedFilters.value.start_ym, appliedFilters.value.end_ym);
    });

    const historyTableStyle = computed(() => {
      const baseWidth = 1342;
      const monthWidth = 112;
      return {
        minWidth: `${baseWidth + (periodMonths.value.length * monthWidth)}px`,
      };
    });

    const displayedSchedules = computed(() => {
      return schedules.value.filter((schedule) => Array.isArray(schedule.work_contents) && schedule.work_contents.length);
    });

    const getMonthlyDetail = (monthlyDetails, periodKey) => {
      if (!monthlyDetails) return null;
      return monthlyDetails[periodKey] || null;
    };

    const calculateTotal = (details) => {
      if (!details) return 0;
      return periodMonths.value.map((periodMonth) => {
        const detail = getMonthlyDetail(details, periodMonth.key);
        return Number(detail?.request_amount || 0);
      }).reduce((sum, amount) => sum + amount, 0);
    };

    const calculateGroupTotal = (workContents = []) => {
      return workContents.reduce((sum, workContent) => sum + calculateTotal(workContent.monthly_details), 0);
    };

    const workRows = computed(() => {
      return displayedSchedules.value.flatMap((schedule) => {
        return schedule.work_contents.map((workContent, index) => ({
          vendorId: schedule.vendor_id,
          vendorName: schedule.vendor_name,
          rowIndex: index,
          buildingName: workContent.building_nm || '未設定',
          customerName: workContent.customer_nm || '未設定',
          type: workContent.type || '未設定',
          businessClassificationName: workContent.business_classification_nm || '未設定',
          workContentName: workContent.work_content || '未設定',
          monthlyDetails: workContent.monthly_details,
          total: calculateTotal(workContent.monthly_details),
        }));
      });
    });

    const hasRows = computed(() => workRows.value.length > 0);

    const monthlyBillTotals = computed(() => {
      const totals = periodMonths.value.map(() => 0);

      workRows.value.forEach((row) => {
        periodMonths.value.forEach((periodMonth, index) => {
          const detail = getMonthlyDetail(row.monthlyDetails, periodMonth.key);
          totals[index] += Number(detail?.request_amount || 0);
        });
      });

      return totals;
    });

    const monthlyTaxes = computed(() => monthlyBillTotals.value.map((total) => total * 0.1));
    const monthlyGrandTotals = computed(() => monthlyBillTotals.value.map((total, index) => total + monthlyTaxes.value[index]));
    const yearlyTotal = computed(() => monthlyBillTotals.value.reduce((sum, total) => sum + total, 0));
    const totalTax = computed(() => monthlyTaxes.value.reduce((sum, tax) => sum + tax, 0));
    const grandYearlyTotal = computed(() => yearlyTotal.value + totalTax.value);

    const topVendor = computed(() => {
      return displayedSchedules.value
        .map((schedule) => ({
          name: schedule.vendor_name,
          total: calculateGroupTotal(schedule.work_contents),
        }))
        .sort((left, right) => right.total - left.total)[0] || null;
    });

    const typeBreakdown = computed(() => {
      const annualCount = workRows.value.filter((row) => row.type === '年間予定業務').length;
      const temporaryCount = workRows.value.filter((row) => row.type === '臨時業務').length;
      return { annualCount, temporaryCount };
    });

    const summaryCards = computed(() => [
      {
        label: '対象業者',
        value: `${displayedSchedules.value.length}社`,
        note: `${workRows.value.length}明細`,
      },
      {
        label: '税抜合計',
        value: formatCurrency(yearlyTotal.value),
        note: selectedPeriodLabel.value,
      },
      {
        label: '税込合計',
        value: formatCurrency(grandYearlyTotal.value),
        note: `消費税 ${formatCurrency(totalTax.value)}`,
      },
      {
        label: '最大支払先',
        value: topVendor.value?.name || '対象なし',
        note: topVendor.value ? formatCurrency(topVendor.value.total) : '対象なし',
      },
      {
        label: '種別内訳',
        value: `${typeBreakdown.value.annualCount} / ${typeBreakdown.value.temporaryCount}`,
        note: '年間予定 / 臨時',
      },
    ]);

    const resultOverviewLabel = computed(() => {
      if (loading.value) return '読み込み中';
      if (!hasRows.value) return '0社 / 0明細';
      return `${displayedSchedules.value.length}社 / ${workRows.value.length}明細`;
    });

    const findOptionLabel = (options, id, nameKey) => {
      if (!id) return '';
      const option = options.find((item) => String(item.id) === String(id));
      return option?.[nameKey] || '';
    };

    const vendorOptionLabel = (vendor) => vendor?.vendor_nm || '';
    const customerOptionLabel = (customer) => customer?.customer_nm || '名称非表示';
    const buildingOptionLabel = (building) => building?.building_nm || '名称非表示';
    const businessClassificationOptionLabel = (classification) => classification?.business_classification_nm || '';
    const businessClassificationInputLabel = (classification) => classification?.business_classification_nm || '';

    const activeFilterChips = computed(() => {
      const filters = appliedFilters.value;
      const chips = [
        { label: '対象期間', value: selectedPeriodLabel.value },
      ];

      const workTypeLabel = workTypeOptions.find((option) => option.value === filters.work_type)?.label;
      if (filters.work_type !== 'all') chips.push({ label: '作業種別', value: workTypeLabel });

      const vendorName = findOptionLabel(vendorOptions.value, filters.vendor_id, 'vendor_nm');
      if (vendorName) chips.push({ label: '業者', value: vendorName });

      const customerName = findOptionLabel(customerOptions.value, filters.customer_id, 'customer_nm');
      if (customerName) chips.push({ label: '顧客', value: customerName });

      const buildingName = findOptionLabel(buildingOptions.value, filters.building_id, 'building_nm');
      if (buildingName) chips.push({ label: '建物', value: buildingName });

      const businessClassificationName = findOptionLabel(
        businessClassificationOptions.value,
        filters.business_classification_id,
        'business_classification_nm'
      );
      if (businessClassificationName) chips.push({ label: '業務区分', value: businessClassificationName });

      const amountStatusLabel = amountStatusOptions.find((option) => option.value === filters.amount_status)?.label;
      if (filters.amount_status) chips.push({ label: '金額状態', value: amountStatusLabel });
      if (filters.keyword) chips.push({ label: 'キーワード', value: filters.keyword });
      if (filters.amount_min) chips.push({ label: '金額下限', value: formatCurrency(filters.amount_min) });
      if (filters.amount_max) chips.push({ label: '金額上限', value: formatCurrency(filters.amount_max) });

      return chips;
    });

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    };

    const formatDateTime = (dateValue) => {
      if (!dateValue) return '';
      return new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dateValue);
    };

    const formatDetailAmount = (monthlyDetails, periodKey) => {
      const detail = getMonthlyDetail(monthlyDetails, periodKey);
      return detail ? formatCurrency(detail.request_amount) : '';
    };

    const buildWorkContentKey = (schedule, workContent, index) => {
      return [
        schedule.vendor_id,
        workContent.type,
        workContent.customer_nm,
        workContent.building_nm,
        workContent.business_classification_nm,
        workContent.work_content,
        index,
      ].join('-');
    };

    const fetchFilterOptions = async () => {
      optionLoadError.value = '';

      try {
        const [
          vendorResponse,
          customerResponse,
          buildingResponse,
          businessClassificationResponse,
        ] = await Promise.all([
          getSelectBoxVendors(),
          getSelectBoxCustomers(),
          getSelectBoxBuildings(),
          getBusinessClassifications(),
        ]);

        vendorOptions.value = Array.isArray(vendorResponse.data) ? vendorResponse.data : [];
        customerOptions.value = Array.isArray(customerResponse.data) ? customerResponse.data : [];
        buildingOptions.value = Array.isArray(buildingResponse.data) ? buildingResponse.data : [];
        businessClassificationOptions.value = Array.isArray(businessClassificationResponse.data)
          ? businessClassificationResponse.data
          : [];
      } catch (error) {
        console.error('検索条件の取得に失敗しました:', error);
        optionLoadError.value = '一部の検索候補を取得できませんでした。';
      }
    };

    const fetchSchedules = async (filters = appliedFilters.value) => {
      loading.value = true;
      errorMessage.value = '';
      exportErrorMessage.value = '';

      try {
        const response = await getVendorAnnualSchedules(buildApiParams(filters));
        schedules.value = Array.isArray(response.data) ? response.data : [];
        appliedFilters.value = { ...filters };
        lastLoadedAt.value = new Date();
      } catch (error) {
        console.error('業者作業履歴の取得に失敗しました:', error);
        schedules.value = [];
        errorMessage.value = error?.response?.data?.error || '業者作業履歴の取得に失敗しました。';
      } finally {
        loading.value = false;
      }
    };

    const applyFilters = async () => {
      const normalizedFilters = normalizeFilters(searchForm.value);
      searchForm.value = { ...normalizedFilters };
      await router.replace({ name: 'VendorWorkHistory', query: buildRouteQuery(normalizedFilters) });
      await fetchSchedules(normalizedFilters);
    };

    const resetFilters = async () => {
      const defaultFilters = createDefaultFilters();
      searchForm.value = { ...defaultFilters };
      await router.replace({ name: 'VendorWorkHistory', query: {} });
      await fetchSchedules(defaultFilters);
    };

    const handleStartYmChange = () => {
      if (!parseYmValue(searchForm.value.start_ym)) {
        searchForm.value.start_ym = createDefaultFilters().start_ym;
      }

      searchForm.value.end_ym = searchForm.value.start_ym;
    };

    const handleEndYmChange = () => {
      if (!parseYmValue(searchForm.value.end_ym)) {
        searchForm.value.end_ym = searchForm.value.start_ym;
      }

      if (compareYmValue(searchForm.value.end_ym, searchForm.value.start_ym) < 0) {
        searchForm.value.end_ym = searchForm.value.start_ym;
      }
    };

    const getSearchConditionValue = (label) => {
      const chip = activeFilterChips.value.find((item) => item.label === label);
      return chip?.value || 'すべて';
    };

    const buildExportRows = () => {
      return workRows.value.map((row) => {
        const exportRow = {
          対象期間: selectedPeriodLabel.value,
          業者名: row.vendorName,
          顧客名: row.customerName,
          建物名: row.buildingName,
          種別: row.type,
          業務区分: row.businessClassificationName,
          作業項目: row.workContentName,
        };

        periodMonths.value.forEach((periodMonth) => {
          const detail = getMonthlyDetail(row.monthlyDetails, periodMonth.key);
          exportRow[periodMonth.label] = detail ? Number(detail.request_amount || 0) : null;
        });

        exportRow.計 = row.total;
        return exportRow;
      });
    };

    const buildExportMetadataRows = (exportedAt) => [
      ['項目', '値'],
      ['対象期間', selectedPeriodLabel.value],
      ['作業種別', getSearchConditionValue('作業種別')],
      ['業者', getSearchConditionValue('業者')],
      ['顧客', getSearchConditionValue('顧客')],
      ['建物', getSearchConditionValue('建物')],
      ['業務区分', getSearchConditionValue('業務区分')],
      ['金額状態', getSearchConditionValue('金額状態')],
      ['キーワード', appliedFilters.value.keyword || 'すべて'],
      ['依頼金額 下限', appliedFilters.value.amount_min ? formatCurrency(appliedFilters.value.amount_min) : '指定なし'],
      ['依頼金額 上限', appliedFilters.value.amount_max ? formatCurrency(appliedFilters.value.amount_max) : '指定なし'],
      ['対象業者数', displayedSchedules.value.length],
      ['明細数', workRows.value.length],
      ['税抜合計', yearlyTotal.value],
      ['消費税', totalTax.value],
      ['税込合計', grandYearlyTotal.value],
      ['出力日時', formatDateTime(exportedAt)],
    ];

    const exportToExcel = () => {
      if (!hasRows.value) return;

      exporting.value = true;
      exportErrorMessage.value = '';

      try {
        const exportedAt = new Date();
        const workbook = XLSX.utils.book_new();
        const detailWorksheet = XLSX.utils.json_to_sheet(buildExportRows());
        const metadataWorksheet = XLSX.utils.aoa_to_sheet(buildExportMetadataRows(exportedAt));

        detailWorksheet['!cols'] = [
          { wch: 10 },
          { wch: 28 },
          { wch: 28 },
          { wch: 28 },
          { wch: 14 },
          { wch: 18 },
          { wch: 36 },
          ...periodMonths.value.map(() => ({ wch: 12 })),
          { wch: 14 },
        ];
        metadataWorksheet['!cols'] = [{ wch: 18 }, { wch: 36 }];

        XLSX.utils.book_append_sheet(workbook, detailWorksheet, '業者作業履歴');
        XLSX.utils.book_append_sheet(workbook, metadataWorksheet, '検索条件');
        XLSX.writeFile(
          workbook,
          `業者作業履歴_${sanitizeFilePart(selectedPeriodLabel.value)}_${formatTimestampForFilename(exportedAt)}.xlsx`,
          { compression: true }
        );
      } catch (error) {
        console.error('Excel出力に失敗しました:', error);
        exportErrorMessage.value = 'Excel出力に失敗しました。';
      } finally {
        exporting.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([
        fetchFilterOptions(),
        fetchSchedules(initialFilters),
      ]);
    });

    return {
      activeFilterChips,
      amountStatusOptions,
      appliedFilters,
      buildingOptions,
      businessClassificationOptions,
      businessClassificationInputLabel,
      businessClassificationOptionLabel,
      buildingOptionLabel,
      calculateGroupTotal,
      calculateTotal,
      customerOptionLabel,
      customerOptions,
      displayedSchedules,
      errorMessage,
      exportErrorMessage,
      exporting,
      exportToExcel,
      formatCurrency,
      formatDateTime,
      formatDetailAmount,
      grandYearlyTotal,
      handleEndYmChange,
      handleStartYmChange,
      hasRows,
      historyTableStyle,
      lastLoadedAt,
      loading,
      monthlyBillTotals,
      monthlyGrandTotals,
      monthlyTaxes,
      optionLoadError,
      periodMonths,
      resetFilters,
      resultOverviewLabel,
      searchForm,
      selectedPeriodLabel,
      summaryCards,
      totalTax,
      vendorOptions,
      vendorOptionLabel,
      workTypeOptions,
      yearlyTotal,
      applyFilters,
      buildWorkContentKey,
    };
  },
};
</script>

<style scoped>
.vendor-work-history-page {
  min-height: 100vh;
  padding: 20px 20px 80px;
  background: #f5f7fa;
  color: #1f2933;
}

.page-header,
.search-panel,
.table-section {
  border: 1px solid #d9e1ea;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(31, 41, 51, 0.06);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.page-eyebrow {
  margin: 0 0 4px;
  color: #52606d;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.page-title-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.page-title-row h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.35;
}

.period-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e7f6f2;
  color: #116149;
  font-size: 13px;
  font-weight: 700;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.search-panel {
  padding: 18px 20px;
  margin-bottom: 16px;
}

.panel-heading,
.table-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.panel-heading h2,
.table-toolbar h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.loaded-at,
.table-toolbar span,
.summary-card small {
  color: #627386;
  font-size: 13px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 14px;
  align-items: end;
}

.filter-field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.filter-field span {
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.filter-field :deep(.relative) {
  min-width: 0;
  width: 100%;
}

.filter-field :deep(.dropdown) {
  z-index: 1200;
}

.filter-field-wide {
  grid-column: span 2;
}

.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.filter-actions .btn {
  min-width: 92px;
}

.active-filter-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.filter-chip {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  max-width: 360px;
  padding: 5px 10px;
  border: 1px solid #c7d7e5;
  border-radius: 999px;
  background: #f7fbff;
  color: #344054;
  font-size: 12px;
}

.filter-chip strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  display: flex;
  min-height: 96px;
  flex-direction: column;
  justify-content: space-between;
  padding: 14px;
  border: 1px solid #d9e1ea;
  border-radius: 8px;
  background: #ffffff;
}

.summary-card span {
  color: #52606d;
  font-size: 13px;
  font-weight: 700;
}

.summary-card strong {
  overflow: hidden;
  color: #1f2933;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-section {
  overflow: hidden;
}

.table-toolbar {
  padding: 16px 18px;
  margin: 0;
  border-bottom: 1px solid #d9e1ea;
}

.table-toolbar-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.table-toolbar-total strong {
  font-size: 20px;
}

.state-panel {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  color: #52606d;
  text-align: center;
}

.state-panel h3,
.state-panel p {
  margin: 0;
}

.state-panel h3 {
  color: #1f2933;
  font-size: 18px;
}

.table-scroll {
  max-height: calc(100vh - 360px);
  min-height: 360px;
  overflow: auto;
  background: #ffffff;
}

.history-table {
  min-width: 1860px;
  width: 100%;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.history-table th,
.history-table td {
  padding: 8px 10px;
  border-right: 1px solid #d8e0e8;
  border-bottom: 1px solid #d8e0e8;
  font-size: 13px;
  line-height: 1.4;
  vertical-align: middle;
}

.history-table thead th {
  position: sticky;
  top: 0;
  z-index: 20;
  background: #263746;
  color: #ffffff;
  font-weight: 700;
  white-space: nowrap;
}

.history-table tbody tr:hover td {
  background: #f6fbff;
}

.history-table tbody tr:hover .sticky-col {
  background: #eef7ff;
}

.sticky-col {
  position: sticky;
  z-index: 12;
  background: #f8fbff;
}

.history-table thead .sticky-col {
  z-index: 32;
  background: #263746;
}

.vendor-col {
  left: 0;
  width: 180px;
  min-width: 180px;
  max-width: 180px;
}

.vendor-total-col {
  left: 180px;
  width: 132px;
  min-width: 132px;
  max-width: 132px;
}

.building-col {
  left: 312px;
  width: 190px;
  min-width: 190px;
  max-width: 190px;
  box-shadow: inset -1px 0 #c4d0dc;
}

.customer-col,
.classification-col {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
}

.type-col {
  width: 116px;
  min-width: 116px;
  max-width: 116px;
}

.work-content-col {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.month-col {
  width: 112px;
  min-width: 112px;
  max-width: 112px;
}

.total-col {
  width: 124px;
  min-width: 124px;
  max-width: 124px;
}

.text-truncate-cell,
.vendor-name-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount-cell {
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.vendor-total-cell,
.row-total-cell {
  font-weight: 700;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.type-badge.annual {
  background: #e7f0ff;
  color: #235a97;
}

.type-badge.temporary {
  background: #fff4df;
  color: #8a4d0f;
}

.history-table tfoot td {
  border-top: 2px solid #aab8c5;
  background: #f8fafc;
  font-weight: 700;
  white-space: nowrap;
}

.history-table tfoot .tax-row td {
  border-top: 1px solid #d8e0e8;
  background: #fffdf4;
}

.history-table tfoot .grand-total-row td {
  border-top: 1px solid #c3aa5a;
  background: #fff6cf;
}

@media (max-width: 1200px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}

@media (max-width: 720px) {
  .vendor-work-history-page {
    padding: 12px 12px 64px;
  }

  .page-header,
  .panel-heading,
  .table-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions,
  .filter-actions {
    width: 100%;
  }

  .header-actions .btn,
  .filter-actions .btn {
    flex: 1;
  }

  .filter-grid,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .filter-field-wide {
    grid-column: span 1;
  }

  .page-title-row h1 {
    font-size: 22px;
  }
}
</style>
